<template>
  <div class="audio-library">
    <PageHeading title="Audiothek" />
    <div class="audio-library__filter">
      <Filter>
        <template #date>
          <input v-model="selectedDate" type="date" placeholder="tt.mm.JJJJ" :max="useFormatDate('archiveDate', new Date().toString())">
        </template>
        <template #search>
          <input v-model="filter.search" type="search" placeholder="Suche">
        </template>
        <template #show>
          <select v-model="filter.show">
            <option v-for="show in filterData.shows.items" :key="show.id" :value="show.id">
              {{ show.name }}
            </option>
          </select>
        </template>
        <template #language>
          <select v-model="filter.language">
            <option v-for="language in filterData.languages.items" :key="language.id" :value="language.id">
              {{ language.name }}
            </option>
          </select>
        </template>
        <template #category>
          <select v-model="filter.tag">
            <option v-for="tag in filterData.tags.items" :key="tag.id" :value="tag.id">
              {{ tag.name }}
            </option>
          </select>
        </template>
      </Filter>
    </div>
    <PageContent>
      <section class="audio-library__content">
        <div class="audio-library__list">
          <AudioLibraryTable :rows="items" />
          <div v-if="!items.length" class="audio-library__empty">
            {{ appConfig.noResults }}
          </div>
        </div>
      </section>
    </PageContent>
    <div class="audio-library__pager">
      <div class="audio-library__pager-inner">
        <Pager
          :is-first-page
          :is-last-page
          :current-page
          :page-count
          @prev="prev"
          @next="next"
          @page-select="onPageSelect"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { startOfDay, startOfHour, isFuture, addDays, endOfDay } from 'date-fns'

const appConfig = useAppConfig()

const items = ref([])
const total = ref(0)
const pageSize = appConfig.defaultPageSizeList

const currentHour = startOfHour(Date.now())
const selectedDate: Ref<Date | string> = ref('')

interface DateRange {
  start: String
  end: String
}

const dateRange: Ref<DateRange> = computed(() => {
  let start = startOfDay(new Date(71, 0))
  let end = currentHour
  if (selectedDate.value !== '') {
    start = startOfDay(selectedDate.value)
    end = startOfDay(addDays(selectedDate.value, 1))
    if (isFuture(endOfDay(selectedDate.value))) {
      end = currentHour
    }
  }

  return {
    start: useFormatDateGraphqlFilter(start),
    end: useFormatDateGraphqlFilter(end)
  }
})

const route = useRoute()

const filter = ref({
  search: '',
  show: '',
  language: '',
  tag: '',
  ...route.query
})

watch(filter.value, async (filter) => {
  await navigateTo({ name: route.name, query: { ...filter }, replace: true })
})

const filterQuery = computed(() => {
  return {
    dateRangeStart: dateRange.value.start,
    dateRangeEnd: dateRange.value.end,
    search: filter.value.search,
    show: [filter.value.show],
    languages: [filter.value.language],
    tags: [filter.value.tag],
    searchEnabled: filter.value.search !== '',
    showEnabled: filter.value.show !== '',
    languagesEnabled: filter.value.language !== '',
    tagsEnabled: filter.value.tag !== ''
  }
})

const { data: filterData } = await useAsyncData('programmeFilter', async () => {
  const data = await useGraphqlQuery('programmeFilter').then((v) => {
    return v.data
  })

  data.shows?.items.unshift({
    id: '',
    name: 'Alle Sendungen'
  })

  data.languages?.items.unshift({
    id: '',
    name: 'Alle Sprachen'
  })

  data.tags?.items.unshift({
    id: '',
    name: 'Alle Tags'
  })
  return data
})

const {
  currentPage,
  pageCount,
  isFirstPage,
  isLastPage,
  prev,
  next,
  offset,
  onPageSelect
} = await usePaginatedFilteredQuery(total, pageSize, fetchData, filterQuery)

const { start, finish } = useLoadingIndicator()

async function fetchData (setData: Boolean = true) {
  start()
  return await useGraphqlQuery('audioLibrary', {
    limit: pageSize,
    offset: offset.value,
    ...filterQuery.value
  }).then((v) => {
    // @todo: refactor this: move to usePaginatedFilteredQuery?
    if (setData) {
      items.value = v.data.episodes.items
      total.value = v.data.episodes.total
    }
    finish()
    return v.data.episodes
  })
}

const { data } = await useAsyncData('audioLibrary', async () => {
  return await fetchData(false)
})

items.value = data.value.items
total.value = data.value.total

useSeoMeta({
  title: 'Audiothek'
})

</script>

<style lang="scss">
@use "~/assets/sass/tools";

.audio-library {
  &__filter {
    margin-top: var(--space--xl);
  }

  &__content {
    @include tools.grid;
    margin-top: var(--space--xxl);
  }

  &__list {
    @include tools.page-grid-columns;
    margin: 0;
    margin-bottom: var(--space--xxl);
    padding-left: 0;
  }

  &__pager {
    @include tools.grid;
  }

  &__pager-inner {
    @include tools.page-grid-columns;
  }
}
</style>
